import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Divider, Flex } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import {
  ErrorAlertWithDrawer,
  Main,
  ObjectHeader,
  Padding,
  SidePanel,
  Tooltip,
} from '../../common/components';
import {
  confirmPanelFormClose,
  Form,
  FormSubmitButton,
} from '../../common/form';

import { CREATE_PARAM } from '../../common/queryParams/create';
import { cleanArray } from '../../common/utils';
import { defaultValidationRule } from './defaultValidationRule';
import translations from './ValidationRuleEditPanel.translations.json';
import { ValidationRuleValidDataType } from '../schema';
import { ValidationRuleValidDataSchema } from '../schema/ValidationRuleDataValidSchema';
import { ValidationRuleFormFields } from './ValidationRuleFormFields';
import { ValidationStatus } from './ValidationStatus';
import { ValidationRuleTestRunModal } from './ValidationRuleTestRunModal';
import { useCheckValidationRule } from './useCheckValidationRule';
import { ValidationRuleErrorDetails } from './ValidationRuleErrorDetails';

const { addTranslations, useTranslation } = i18nNsFactory('algorithms-table');
addTranslations(translations);

export interface ValidationRuleEditPanelProps {
  onClose: () => void;
  onSubmit: (validationRule: ValidationRuleValidDataType) => void;
  validationRule?: Partial<ValidationRuleValidDataType>;
  validationRuleIsDirty?: boolean;
}

type ValidationRuleFormInnerProps = Pick<
  ValidationRuleEditPanelProps,
  'onClose' | 'validationRule' | 'validationRuleIsDirty'
> & { groupCodes: string[] };

function ValidationRuleFormInner({
  groupCodes,
  onClose,
  validationRule,
  validationRuleIsDirty,
}: ValidationRuleFormInnerProps) {
  const { t } = useTranslation();
  const { isDirty } = useFormState();
  const [testRunData, setTestRunData] =
    useState<Partial<ValidationRuleValidDataType> | null>(null);
  const isNew = !validationRule || !validationRule.id || validationRule.id < 0;
  const disableActionButtons = isNew || isDirty || validationRuleIsDirty;
  const { handleCheck, mutationErrors, isChecking } =
    useCheckValidationRule(validationRule);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose();
    }
  }, [onClose, isDirty]);

  return (
    <SidePanel
      minWidth={700}
      side="right"
      collapsedWidth={700}
      width={1786}
      mode="modal"
    >
      <SidePanelWrapper>
        <AlertWrapper>
          <ErrorAlertWithDrawer
            errorAction={t('action.validating')}
            validationMessagesMap={mutationErrors}
            renderErrorMeta={(_, messages, index) => (
              <ValidationRuleErrorDetails index={index} messages={messages} />
            )}
            errorPrewrap
          />
        </AlertWrapper>
        <Padding>
          <Flex align="end" justify="space-between">
            <ObjectHeader
              upperText={validationRule?.code}
              lowerText={t('validation.rule.heading')}
            />
            <ValidationStatus itemId={validationRule?.statusId ?? undefined} />
          </Flex>
        </Padding>
        <Divider style={{ margin: 0 }} />
        <FormWrapper vertical>
          <Scrollable>
            <Padding>
              <ValidationRuleFormFields groupCodes={groupCodes} />
            </Padding>
          </Scrollable>
        </FormWrapper>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            <Button id="revert-validationRule" onClick={handleCancel}>
              {t('button.cancel')}
            </Button>
            {validationRule && (
              <>
                <Tooltip title={disableActionButtons ? t('tooltip.save') : ''}>
                  <Button
                    id="test-run-validation-rule"
                    disabled={disableActionButtons}
                    onClick={() => setTestRunData(validationRule)}
                  >
                    {t('button.testRun')}
                  </Button>
                </Tooltip>
                <Tooltip title={disableActionButtons ? t('tooltip.save') : ''}>
                  <Button
                    id="check-validation-rule"
                    disabled={disableActionButtons}
                    loading={isChecking}
                    onClick={handleCheck}
                  >
                    {t('button.check')}
                  </Button>
                </Tooltip>
              </>
            )}
            <FormSubmitButton id="submit-validationRule">
              {t('button.ok')}
            </FormSubmitButton>
            {testRunData && (
              <ValidationRuleTestRunModal
                validationRule={testRunData}
                onClose={() => setTestRunData(null)}
              />
            )}
          </FullWidthFlex>
        </Main.Footer>
      </SidePanelWrapper>
    </SidePanel>
  );
}

export function ValidationRuleEditPanel({
  onClose,
  onSubmit,
  validationRule,
  validationRuleIsDirty,
}: ValidationRuleEditPanelProps) {
  const groupCodes = useValidationGroupCodes();
  const handleSubmit = useCallback(
    (formData: ValidationRuleValidDataType) => {
      onSubmit(formData);
    },
    [onSubmit],
  );

  return (
    <Form
      schema={ValidationRuleValidDataSchema}
      defaultValues={validationRule ?? defaultValidationRule()}
      onSubmit={handleSubmit}
      idAttributePrefix={`validationRule-${validationRule?.id ?? CREATE_PARAM}`}
    >
      <ValidationRuleFormInner
        groupCodes={groupCodes}
        onClose={onClose}
        validationRule={validationRule}
        validationRuleIsDirty={validationRuleIsDirty}
      />
    </Form>
  );
}

function useValidationGroupCodes() {
  const validationRules = useWatch({
    name: 'validationRules',
  }) as ValidationRuleValidDataType[];
  return useMemo(
    () =>
      cleanArray(
        Array.from(new Set(validationRules?.map(({ groupCode }) => groupCode))),
      ),
    [validationRules],
  );
}

const AlertWrapper = styled.div`
  max-height: 72px;
  overflow-y: auto;
`;

const FormWrapper = styled(Flex)`
  overflow-y: hidden;
`;

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

const Scrollable = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
`;

const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto auto auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow-y: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;
